import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import type { ActionResponse, DirectoryUserDto, UserAttributeDto, UserAttributesDto } from '../api/types';

/** Leere Eingaben und fehlende Werte gleich behandeln, damit „nichts geändert" stimmt. */
const norm = (v?: string | null) => (v ?? '').trim();

export function UserAttributesPage() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'users.update')?.ticketMode ?? 'None';

  const [user, setUser] = useState<DirectoryUserDto | null>(null);
  const [data, setData] = useState<UserAttributesDto | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [ticket, setTicket] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResponse | null>(null);

  /** Attribute eines Benutzers laden und den Entwurf mit den Ist-Werten füllen. */
  async function load(u: DirectoryUserDto) {
    setUser(u);
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const attrs = await api.getUserAttributes(u.id);
      setData(attrs);
      setDraft(Object.fromEntries(attrs.attributes.map((a) => [a.key, norm(a.value)])));
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Abschnitte in der Reihenfolge, in der die API sie liefert.
  const groups = useMemo(() => {
    const map = new Map<string, UserAttributeDto[]>();
    for (const a of data?.attributes ?? []) {
      const list = map.get(a.group);
      if (list) list.push(a);
      else map.set(a.group, [a]);
    }
    return [...map.entries()];
  }, [data]);

  // Nur echte Abweichungen gehen an die API — leerer Wert leert das Feld in Entra.
  const changed = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const a of data?.attributes ?? []) {
      if (!a.editable) continue;
      const next = norm(draft[a.key]);
      if (next !== norm(a.value)) out[a.key] = next === '' ? null : next;
    }
    return out;
  }, [data, draft]);

  const changedKeys = Object.keys(changed);
  const locked = data?.onPremisesSynced ?? false;

  async function save() {
    if (!data || changedKeys.length === 0) return;
    if (ticketMode === 'Required' && !ticket.trim()) {
      setError('Für diese Aktion ist eine Ticketnummer erforderlich (Governance).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.updateUserAttributes(data.id, {
        attributes: changed,
        ticketNumber: ticket.trim() || undefined,
      });
      setResult(res);
      // Nach direkter Ausführung den Ist-Stand neu laden; bei Freigabepflicht
      // bleibt der Entwurf stehen, da noch nichts geschrieben wurde.
      if (res.status === 'Executed' && user) await load(user);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    if (!data) return;
    setDraft(Object.fromEntries(data.attributes.map((a) => [a.key, norm(a.value)])));
    setError(null);
  }

  return (
    <div>
      <PageHeader
        title="Benutzer anpassen"
        description="Verzeichnis-Attribute eines Benutzers einsehen und die in Entra änderbaren Felder pflegen."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, maxWidth: 860 }}>
        <Field label="Benutzer">
          <UserPicker onSelect={(u) => u && void load(u)} placeholder="Person suchen…" />
        </Field>

        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        {result ? (
          <MessageBar intent={result.status === 'PendingApproval' ? 'info' : 'success'}>
            <MessageBarBody>
              <MessageBarTitle>{result.status === 'PendingApproval' ? 'Antrag eingereicht' : 'Erledigt'}</MessageBarTitle>
              {result.status === 'PendingApproval'
                ? 'Die Änderung ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".'
                : result.message}
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {loading ? <Spinner label="Lade Attribute…" /> : null}

        {data ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacingHorizontalM,
                flexWrap: 'wrap',
              }}
            >
              <Text weight="semibold">
                <span className="pii">{data.displayName}</span>
              </Text>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                <span className="pii">{data.userPrincipalName}</span>
              </Caption1>
              {data.userType ? (
                <Badge appearance="tint" color={data.userType === 'Guest' ? 'warning' : 'brand'}>
                  {data.userType === 'Guest' ? 'Gast' : 'Mitglied'}
                </Badge>
              ) : null}
              <Badge appearance="tint" color={data.accountEnabled ? 'success' : 'danger'}>
                {data.accountEnabled ? 'Anmeldung aktiv' : 'gesperrt'}
              </Badge>
            </div>

            {locked ? (
              <MessageBar intent="warning">
                <MessageBarBody>
                  <MessageBarTitle>Aus lokalem Active Directory synchronisiert</MessageBarTitle>
                  Die Attribute dieses Kontos werden im lokalen Verzeichnis gemastert und lassen sich in Entra nicht
                  ändern. Die Werte sind deshalb nur lesbar.
                </MessageBarBody>
              </MessageBar>
            ) : null}

            {groups.map(([group, attrs]) => (
              <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Text weight="semibold" style={{ color: tokens.colorNeutralForeground2 }}>
                  {group}
                </Text>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: tokens.spacingHorizontalL,
                  }}
                >
                  {attrs.map((a) =>
                    a.editable ? (
                      <Field key={a.key} label={a.label} hint={a.hint ?? undefined}>
                        <Input
                          value={draft[a.key] ?? ''}
                          maxLength={a.maxLength > 0 ? a.maxLength : undefined}
                          onChange={(_, d) => setDraft((p) => ({ ...p, [a.key]: d.value }))}
                        />
                      </Field>
                    ) : (
                      // Nicht änderbare Felder bewusst mitzeigen, damit erkennbar
                      // ist, was Entra hier nicht zulässt.
                      <Field key={a.key} label={a.label} hint={a.hint ?? undefined}>
                        <Input value={a.value ?? '—'} disabled />
                      </Field>
                    ),
                  )}
                </div>
              </div>
            ))}

            {!locked ? (
              <>
                {ticketMode !== 'None' ? (
                  <Field
                    label="Ticketnummer"
                    required={ticketMode === 'Required'}
                    hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
                  >
                    <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" />
                  </Field>
                ) : null}

                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                  <Button appearance="primary" disabled={busy || changedKeys.length === 0} onClick={save}>
                    {busy ? 'Bitte warten…' : 'Änderungen speichern'}
                  </Button>
                  <Button appearance="secondary" disabled={busy || changedKeys.length === 0} onClick={reset}>
                    Zurücksetzen
                  </Button>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {changedKeys.length === 0
                      ? 'Keine Änderungen.'
                      : `${changedKeys.length} geändert: ${changedKeys
                          .map((k) => data.attributes.find((a) => a.key === k)?.label ?? k)
                          .join(', ')}`}
                  </Caption1>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
